import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useFetcher } from 'react-router';

import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { RoccoLink } from '@rocco/components/link';
import type { CategoryNeighbour } from '~/routes/remix-api.product.neighbours';
import {
    NEXT_PRODUCT_GALLERY_API_ROUTE,
    type NextGallerySlide,
    type loader as nextGalleryLoader,
} from '~/routes/remix-api.product.next-gallery';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import {
    NEXT_LISTING_START_SCALE,
    useNextListingTurn,
} from '~/ui-rocco/pages/detail-shared/use-next-listing-turn';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import {
    GALLERY_SLIDE_GUTTER_REM,
    GALLERY_SLIDE_HEIGHT_REM,
    GALLERY_SLIDE_PAD_REM,
    PRODUCT_GALLERY_SLIDE_SIZES,
    galleryRowLoops,
    galleryTrackOffset,
    mobileSlideWidthVw,
} from './gallery-geometry';
import { useBoundedRail } from './use-gallery-rail';

/**
 * The breath above the section, owned by the section so that a product with
 * no next one takes its spacer down with it — left on the page it stacks
 * into a dead band above the footer.
 *
 * `SectionSpacer` without `unit="rem"` on purpose. Its rem height switches on
 * at `md` while the root font-size stays on its mobile base until `lg`, so
 * between 768px and 1023px a rem gap lands at around three times what the
 * design asks for. Its viewport height resolves to exactly the same figure
 * from `lg` up and stays proportional below, so the gap reads as one rhythm
 * across every width.
 */
/** The page's standard closing gap, in Figma px at the 1920 desktop frame. */
const CLOSING_GAP = 120;

const ClosingBreath = ({ mobile }: { mobile: number }) => (
    <SectionSpacer
        height={CLOSING_GAP}
        mobileHeight={mobile}
        className="bg-ground"
    />
);

/**
 * The product title exactly as the page's own hero sets it, so the next
 * product's name is standing where its page will put it before that page
 * arrives. Any drift here is a jump the reader sees at the turn.
 */
const TITLE_TYPE = cn(
    'm-0 font-sans font-medium tracking-[-0.03em] text-[#323232]',
    'max-md:text-[2.25rem] max-md:leading-[1.05]',
    'md:text-[5rem] md:leading-[4.25rem]',
);

/** The hero's byline row, for the same reason. */
const BYLINE_ROW = cn(
    'font-sans font-medium',
    'max-md:mt-6 max-md:flex max-md:flex-col max-md:gap-1 max-md:text-base',
    'md:mt-[3rem] md:grid md:grid-cols-[51rem_1fr] md:items-baseline md:text-[1.5rem]',
);

/** The invitation is gone by this much of the growth. */
const LABEL_FADE_BY = 0.45;

/** And the next product's own title takes over from here. */
const TITLE_FADE_FROM = 0.55;

/**
 * How many of the row's last slides stand wrapped to the left of the first.
 *
 * A looping gallery rests with its last slides wrapped round to the left of
 * the centred first one. Two are always enough to cover that side: the left
 * half of the widest design frame is 60rem, and the narrowest slide with its
 * gutters is 37rem.
 */
const WRAPPED_SLIDES = 2;

const setOpacity = (node: HTMLElement | null, value: number) => {
    if (node) node.style.opacity = String(value);
};

/**
 * The foot of the product page: the next product, growing as the reader
 * arrives at it, and then becoming that page.
 *
 * What grows is the next page's own opening — its title where its hero puts
 * it, its gallery row at the gallery's own geometry, in the gallery's own
 * renditions — laid out at full size and scaled down from there. So by the
 * time the row is full size, the reader is looking at the top of the next
 * page, and the turn is a swap of one document for an identical one.
 *
 * The turn itself — the warm on approach, the readiness gates, the settled
 * commit at the scroll clamp and the landing — is `useNextListingTurn`,
 * shared with every detail page that ends on one. This section owns what
 * the reader sees: the title trading places with the invitation, and the
 * gallery row at the gallery's own geometry.
 *
 * The section is not remounted between products, so everything it holds is
 * re-seeded when the product under it changes.
 *
 * It renders the breath above itself as well as the section, so a product
 * with no next one — no category, or nothing left in it — leaves no gap at
 * all, and keeps its footer.
 */
export const NextProductTransition = ({
    next,
}: {
    /** The product after this one in its category listing, once looked up. */
    next: CategoryNeighbour | null | undefined;
}) => {
    const { log } = useTracker();

    const labelRef = useRef<HTMLParagraphElement>(null);
    const titleRef = useRef<HTMLParagraphElement>(null);
    const bylineRef = useRef<HTMLDivElement>(null);

    // The next product's gallery as its page lays it out. Loaded on arrival,
    // so the row (and the browser's cache of its renditions) is in place long
    // before the reader reaches it.
    const gallery = useFetcher<typeof nextGalleryLoader>();
    const galleryLoad = gallery.load;
    const nextLink = next?.link;
    useEffect(() => {
        if (!nextLink) return;
        const params = new URLSearchParams({ link: nextLink });
        galleryLoad(`${NEXT_PRODUCT_GALLERY_API_ROUTE}?${params}`);
    }, [galleryLoad, nextLink]);

    // The gallery's own row once it has answered for this product; until then
    // the listing's crops stand in, at the shape of a square card.
    const answered =
        gallery.data && gallery.data.link === nextLink ? gallery.data : null;
    const slides: NextGallerySlide[] = answered
        ? answered.slides
        : (next?.images ?? []).map((image, index) => ({
              id: index,
              src: image.src,
              ratio: 1,
              multiplied: image.contained,
          }));

    const { sectionRef, frameRef } = useNextListingTurn({
        link: nextLink,
        answered: Boolean(answered),
        active: Boolean(next) && slides.length > 0,
        anchorSelector: '[data-product-gallery]',
        // The arrival fires its own ViewProduct; this is what marks it as
        // the turn's doing rather than a tile's or the island's.
        onCommit: () =>
            log('NextProductTurn', {
                url: new URL(window.location.href),
                data: { NextLink: nextLink ?? '', Trigger: 'auto' },
            }),
        // The two titles trade places over the growth: the invitation goes
        // as the thing itself arrives, so what is left at the end is the
        // next page's own title over its own gallery.
        onProgress: (progress) => {
            setOpacity(
                labelRef.current,
                Math.max(0, 1 - progress / LABEL_FADE_BY),
            );
            const shown = Math.min(
                Math.max(
                    (progress - TITLE_FADE_FROM) / (1 - TITLE_FADE_FROM),
                    0,
                ),
                1,
            );
            setOpacity(titleRef.current, shown);
            setOpacity(bylineRef.current, shown);
        },
    });

    if (!next || slides.length === 0) return null;

    return (
        <>
            {/* A long breath before the page's last offer. Outside the
                section rather than inside it, so the section still opens
                exactly as the next page does and the growth still measures
                from its own top edge. Nothing follows the section: it is the
                end of the document (the footer stands down while it renders),
                so the scroll clamp lands on the grown row. */}
            <ClosingBreath mobile={48} />
            {/* Exactly one viewport tall: the scroll clamp then stops with
                the title in place rather than past it, clipping the gallery
                at the fold exactly as the next page's own fold will. On a
                viewport taller than the content, the height pads instead,
                so the clamp still lands the section's top in place. */}
            <section
                ref={sectionRef}
                aria-label={`Next product: ${next.title}`}
                className="h-screen w-full overflow-hidden bg-ground"
            >
                {/* The next page's hero, to the rem: the label and the title
                    share one line so the swap between them is a fade and not a
                    jump, and the byline under it is what the hero puts there. */}
                <div className="max-md:px-6 max-md:pt-8 md:px-[3rem] md:pt-[0.6rem]">
                    <div className={CONTENT_CAP_PADDED}>
                        <div className="relative max-md:mt-4 md:mt-[3.75rem] md:-ml-[0.5rem]">
                            <p ref={labelRef} className={TITLE_TYPE}>
                                Next Product
                            </p>
                            <p
                                ref={titleRef}
                                aria-hidden="true"
                                style={{ opacity: 0 }}
                                className={cn(TITLE_TYPE, 'absolute inset-0')}
                            >
                                {next.title}
                            </p>
                        </div>
                        <div
                            ref={bylineRef}
                            aria-hidden="true"
                            style={{ opacity: 0 }}
                            className={BYLINE_ROW}
                        >
                            {/* The hero's Back control, as drawn there: the
                                triangle sets the row's baseline, and a row set
                                without it is two pixels shorter, which is where
                                the gallery under it would land. */}
                            <span className="inline-flex items-center gap-[0.5em] justify-self-start leading-[1.2] text-[#b1b1b1]">
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 6 10"
                                    className="h-[0.5em] w-[0.3em] shrink-0"
                                    fill="currentColor"
                                >
                                    <polygon points="6,0 6,10 0,5" />
                                </svg>
                                Back
                            </span>
                            <div className="md:flex md:items-baseline md:justify-between">
                                {next.supplier ? (
                                    <p className="m-0 leading-[1.2] text-[#323232]">
                                        <span className="mr-[0.25rem] text-[#b1b1b1]">
                                            by
                                        </span>{' '}
                                        {next.supplier}
                                    </p>
                                ) : (
                                    <span />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <SectionSpacer
                    height={48}
                    mobileHeight={24}
                    className="bg-ground"
                    unit="rem"
                />

                {/* A link, not only a scroll target. The growth is the
                    invitation, but the next product has to be reachable by a
                    click and by the keyboard whether or not the page ever turns
                    itself. */}
                <RoccoLink
                    to={next.link}
                    // No link prefetch: Safari ignores the tag, so the scroll
                    // handler warms the route data with a plain fetch instead.
                    aria-label={`Go to ${next.title}`}
                    className="block no-underline"
                    onClick={() =>
                        log('NextProductTurn', {
                            url: new URL(window.location.href),
                            data: { NextLink: next.link, Trigger: 'click' },
                        })
                    }
                >
                    {/* Laid out at the size it ends on and scaled down from
                        there, so growing costs one composited transform rather
                        than a re-layout of the row. */}
                    <div
                        ref={frameRef}
                        style={{
                            transform: `scale(${NEXT_LISTING_START_SCALE})`,
                        }}
                        className="origin-top will-change-transform"
                    >
                        <NextGalleryRow slides={slides} title={next.title} />
                    </div>
                </RoccoLink>
            </section>
        </>
    );
};

/**
 * The gallery row as the gallery lays it out at rest: a looping row has its
 * last slides wrapped round to the left of the centred first one, a row that
 * fits sits centred whole, and a lone clear-cut render is one wide band.
 */
const NextGalleryRow = ({
    slides,
    title,
}: {
    slides: NextGallerySlide[];
    title: string;
}) => {
    const boundedRail = useBoundedRail();
    const first = slides[0];
    if (!first) return null;
    const isMulti = slides.length > 1;

    if (!isMulti && first.multiplied && first.ratio >= 1) {
        return (
            <div className="relative w-full overflow-x-clip bg-ground">
                <div className="md:px-[3rem]">
                    <div className="relative mx-auto h-[84vw] w-[calc(100vw-2rem)] overflow-hidden rounded-tile bg-[var(--color-clearcut-fill)] md:mx-0 md:h-[54.375rem] md:w-full">
                        <Image
                            src={first.src}
                            alt={title}
                            fill
                            fit="contain"
                            multiplied
                            sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                            loading="lazy"
                            fadeIn={false}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const widthRem = (slide: NextGallerySlide) =>
        GALLERY_SLIDE_HEIGHT_REM * slide.ratio + GALLERY_SLIDE_GUTTER_REM;
    const trackRem = slides.reduce(
        (total, slide) => total + widthRem(slide),
        0,
    );
    // The bounded rail never wraps, so nothing is carried round to the left
    // of the first slide and the row starts on the gutter.
    const loops = !boundedRail && galleryRowLoops(slides.length, trackRem);
    const wrapped = loops ? slides.slice(-WRAPPED_SLIDES) : [];
    const wrappedRem = wrapped.reduce(
        (total, slide) => total + widthRem(slide),
        0,
    );
    // Plus the gutters the slide carries, so the grown row rests where the
    // gallery does — the page turn under it only stays invisible while the
    // two offsets agree.
    const firstRem =
        GALLERY_SLIDE_HEIGHT_REM * first.ratio + GALLERY_SLIDE_GUTTER_REM;
    // A bounded row of more than one slide rests flush inside the gutter, with
    // no transform of its own; everything else rests where the gallery parks it.
    const offset =
        boundedRail && isMulti
            ? undefined
            : loops
              ? `calc((100% - ${firstRem}rem) / 2 - ${wrappedRem}rem)`
              : galleryTrackOffset(firstRem, trackRem);

    return (
        <div
            className={cn(
                'relative w-full overflow-x-clip bg-ground',
                '[--g-pad:1rem]',
                boundedRail && 'md:[--g-pad:calc(3rem_-_var(--g-slide-pad))]',
            )}
            style={
                {
                    '--g-slide-pad': `${GALLERY_SLIDE_PAD_REM}rem`,
                } as CSSProperties
            }
        >
            <div
                className={cn(
                    'h-full overflow-hidden',
                    boundedRail
                        ? 'px-[var(--g-pad)]'
                        : 'max-md:px-[var(--g-pad)]',
                )}
            >
                <div
                    className={cn(
                        'flex h-full max-md:gap-3',
                        offset && 'md:[transform:translateX(var(--g-offset))]',
                    )}
                    style={{ '--g-offset': offset } as CSSProperties}
                >
                    {[...wrapped, ...slides].map((slide, index) => (
                        <div
                            key={`${slide.id}-${index}`}
                            className="shrink-0 grow-0 basis-auto px-0 md:px-[var(--g-slide-pad)]"
                            style={
                                {
                                    '--g-slide-w': `${GALLERY_SLIDE_HEIGHT_REM * slide.ratio}rem`,
                                    '--g-slide-w-m': isMulti
                                        ? `${mobileSlideWidthVw(slide.ratio)}vw`
                                        : 'calc(100vw - 2rem)',
                                } as CSSProperties
                            }
                        >
                            <div className="relative h-[84vw] w-[var(--g-slide-w-m)] overflow-hidden rounded-tile bg-[var(--color-clearcut-fill)] md:h-[54.375rem] md:w-[var(--g-slide-w)]">
                                <Image
                                    src={slide.src}
                                    alt={index === wrapped.length ? title : ''}
                                    fill
                                    fit={slide.multiplied ? 'contain' : 'cover'}
                                    multiplied={slide.multiplied}
                                    sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                                    // The first slide is the one the turn
                                    // lands on, so it is fetched on arrival;
                                    // the rest as the row nears the screen.
                                    loading={
                                        index === wrapped.length
                                            ? 'eager'
                                            : 'lazy'
                                    }
                                    fadeIn={false}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
